import { ArrowLeft, Loader2, PlusCircle, Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import { Suspense, use, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, SubmitHandler } from 'react-hook-form';
import { Link, useBlocker, useParams } from 'react-router-dom';

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/alert-dialog';
import { Button } from '@/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/card';
import { GridBackground } from '@/components/grid-background';
import { Input } from '@/components/input';
import { Label } from '@/components/label';
import { LoadingFallback } from '@/components/loading-fallback';
import { toast } from '@/components/toast';
import { TitleCharCount } from '@/features/quiz-editor/components/char-counters';
import { ImageSelectionDialog } from '@/features/quiz-editor/components/image-selection-dialog';
import { QuizQuestionCard } from '@/features/quiz-editor/components/quiz-question-card';
import { useQuizForm } from '@/features/quiz-editor/hooks/use-quiz-form';
import { useViewTransitionNavigate } from '@/hooks/ui/use-view-transition-navigate';
import { useCreateQuiz, useCustomQuizzes, useGenerateQuestion, useQuizDetail, useUpdateQuiz } from '@/hooks/use-api';
import { useUserId } from '@/hooks/use-user-id';
import { cn } from '@/lib/utilities';
import { LIMITS, type QuizFormInput } from '@shared/validation';

import type { Question } from '@shared/types';

type QuizFormData = {
	title: string;
	questions: Question[];
};

/**
 * Main page component - conditionally renders EditQuizForm or NewQuizForm based on quizId
 */
export function QuizEditorPage() {
	const { quizId } = useParams<{ quizId?: string }>();

	return (
		<div className="relative isolate min-h-screen bg-muted/50">
			<Helmet>
				<title>{quizId ? 'Edit Quiz - Timoot' : 'Create Quiz - Timoot'}</title>
			</Helmet>
			<GridBackground className="-z-10" />
			{quizId ? (
				<Suspense fallback={<LoadingFallback message="Loading quiz..." />}>
					<EditQuizForm quizId={quizId} />
				</Suspense>
			) : (
				<NewQuizForm />
			)}
		</div>
	);
}

/**
 * Form for editing an existing quiz - uses Suspense to load quiz data
 */
function EditQuizForm({ quizId }: { quizId: string }) {
	const { userId } = useUserId();

	const quizQuery = useQuizDetail(userId, quizId);
	const quizData = use(quizQuery.promise);

	const initialFormData = useMemo(
		(): QuizFormInput => ({
			...quizData,
			questions: quizData.questions.map((q) => ({
				...q,
				options: q.options.map((opt) => ({ value: opt })),
				correctAnswerIndex: String(q.correctAnswerIndex),
				backgroundImage: q.backgroundImage,
			})),
		}),
		[quizData],
	);

	return <QuizEditorForm quizId={quizId} initialData={initialFormData} />;
}

/**
 * Form for creating a new quiz - no data fetching needed
 */
function NewQuizForm() {
	const defaultData: QuizFormInput = {
		title: '',
		questions: [
			{ text: '', options: [{ value: '' }, { value: '' }], correctAnswerIndex: '0', isDoublePoints: false, backgroundImage: undefined },
		],
	};

	return <QuizEditorForm initialData={defaultData} />;
}

interface QuizEditorFormProperties {
	quizId?: string;
	initialData: QuizFormInput;
}

/**
 * Shared form component for both edit and create flows
 */
function QuizEditorForm({ quizId, initialData }: QuizEditorFormProperties) {
	const navigate = useViewTransitionNavigate();
	const { userId } = useUserId();

	const { methods, fieldArray } = useQuizForm({ defaultValues: initialData });
	const {
		control,
		handleSubmit,
		reset,
		getValues,
		setValue,
		formState: { errors, isSubmitting, isDirty },
	} = methods;
	const { fields, append, remove, move } = fieldArray;

	// React Query hooks
	const { data: customQuizzes = [] } = useCustomQuizzes(userId);
	const createQuizMutation = useCreateQuiz();
	const updateQuizMutation = useUpdateQuiz();
	const generateQuestionMutation = useGenerateQuestion();

	const isGeneratingQuestion = generateQuestionMutation.isPending;

	// Track intentional navigation after save to skip the blocker
	const skipBlockerReference = useRef(false);

	// Block navigation when there are unsaved changes
	const blocker = useBlocker(() => isDirty && !skipBlockerReference.current);

	// Warn before browser/tab close when there are unsaved changes
	// NOTE: This useEffect is necessary - it's an event listener, not data fetching
	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (isDirty) {
				event.preventDefault();
			}
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [isDirty]);

	const onSubmit: SubmitHandler<QuizFormInput> = async (data) => {
		const processedData: QuizFormData = {
			...data,
			questions: data.questions.map((q) => ({
				text: q.text,
				options: q.options.map((opt) => opt.value),
				correctAnswerIndex: Number.parseInt(q.correctAnswerIndex, 10),
				isDoublePoints: q.isDoublePoints,
				backgroundImage: q.backgroundImage,
			})),
		};

		const onSuccess = (result: { title: string } | undefined) => {
			toast.success(`Quiz "${result?.title}" saved successfully!`);
			reset(data);
			skipBlockerReference.current = true;
			navigate('/');
		};

		if (quizId) {
			updateQuizMutation.mutate(
				{ header: { 'x-user-id': userId }, param: { id: quizId }, json: { ...processedData, id: quizId } },
				{ onSuccess, onError: (error) => toast.error(error.message || 'Failed to save quiz') },
			);
		} else {
			if (customQuizzes.length >= LIMITS.MAX_QUIZZES_PER_USER) {
				toast.error(`You have reached the limit of ${LIMITS.MAX_QUIZZES_PER_USER} quizzes.`);
				return;
			}
			createQuizMutation.mutate(
				{ header: { 'x-user-id': userId }, json: processedData },
				{ onSuccess, onError: (error) => toast.error(error.message || 'Failed to save quiz') },
			);
		}
	};

	const addQuestion = () =>
		append({
			text: '',
			options: [{ value: '' }, { value: '' }],
			correctAnswerIndex: '0',
			isDoublePoints: false,
			backgroundImage: undefined,
		});

	const generateQuestion = () => {
		const title = getValues('title');
		if (!title.trim()) {
			toast.error('Please enter a quiz title first');
			return;
		}

		const currentQuestions = getValues('questions');
		if (currentQuestions.length >= LIMITS.QUESTIONS_MAX) {
			toast.error(`Cannot add more than ${LIMITS.QUESTIONS_MAX} questions`);
			return;
		}

		generateQuestionMutation.mutate(
			{
				header: { 'x-user-id': userId },
				json: {
					title,
					existingQuestions: currentQuestions.map((q) => ({
						text: q.text,
						options: q.options.map((opt) => opt.value),
						correctAnswerIndex: Number(q.correctAnswerIndex),
					})),
				},
			},
			{
				onSuccess: (data) => {
					append({
						text: data.text,
						options: data.options.map((opt) => ({ value: opt })),
						correctAnswerIndex: String(data.correctAnswerIndex),
						isDoublePoints: false,
					});
				},
				onError: (error) => {
					toast.error(error.message || 'Failed to generate question');
				},
			},
		);
	};

	// Image selection state
	const [imageSelectionState, setImageSelectionState] = useState<{
		isOpen: boolean;
		questionIndex: number | undefined;
		currentImage: string | undefined;
	}>({ isOpen: false, questionIndex: undefined, currentImage: undefined });

	return (
		<>
			<div
				className={`
					relative mx-auto max-w-4xl px-4 py-12
					sm:px-6
					lg:px-8
				`}
			>
				<FormProvider {...methods}>
					<form onSubmit={(event) => handleSubmit(onSubmit)(event)} className={`space-y-8`}>
						<div className="flex items-center gap-4">
							<Button variant="subtle" size="icon" className="size-12" asChild aria-label="Back">
								<Link to="/" viewTransition>
									<ArrowLeft className="size-5" />
								</Link>
							</Button>
							<h1
								className={`
									font-display text-2xl font-bold
									sm:text-4xl
								`}
							>
								{quizId ? 'Edit Quiz' : 'Create a New Quiz'}
							</h1>
						</div>
						<Card>
							<CardHeader>
								<CardTitle>Quiz Details</CardTitle>
							</CardHeader>
							<CardContent>
								<Label htmlFor="title">Quiz Title</Label>
								<div className="relative">
									<Input
										id="title"
										{...methods.register('title')}
										placeholder="e.g., 'Fun Facts Friday'"
										className="pr-16 text-lg"
										maxLength={LIMITS.QUIZ_TITLE_MAX}
									/>
									<TitleCharCount control={control} />
								</div>
								{errors.title && <p className="mt-1 text-sm text-red">{errors.title.message}</p>}
							</CardContent>
						</Card>

						{fields.map((field, index) => (
							<QuizQuestionCard
								key={field.id}
								index={index}
								id={field.id}
								move={move}
								remove={remove}
								isFirst={index === 0}
								isLast={index === fields.length - 1}
								onOpenImageDialog={(index_) => {
									const currentImage = getValues(`questions.${index_}.backgroundImage`);
									setImageSelectionState({ isOpen: true, questionIndex: index_, currentImage });
								}}
							/>
						))}

						{fields.length < LIMITS.QUESTIONS_MAX && (
							<div
								className={`
									flex flex-col gap-4
									sm:flex-row
								`}
							>
								<Button
									type="button"
									variant="subtle"
									className="
										flex-1 py-4 text-base
										sm:py-8 sm:text-lg
									"
									onClick={addQuestion}
								>
									<PlusCircle className="mr-2 size-6" /> Add Question
								</Button>
								<Button
									type="button"
									variant="accent"
									onClick={generateQuestion}
									disabled={isGeneratingQuestion}
									className={cn(
										`
											flex-1 overflow-hidden py-4 text-base
											sm:py-8 sm:text-lg
										`,
										isGeneratingQuestion && 'cursor-wait opacity-100!',
									)}
								>
									{isGeneratingQuestion && (
										<>
											<motion.div
												aria-hidden="true"
												className="pointer-events-none absolute inset-0 opacity-25"
												style={{
													backgroundImage:
														'repeating-linear-gradient(45deg, rgba(0,0,0,0.18) 0, rgba(0,0,0,0.18) 6px, transparent 6px, transparent 14px)',
													backgroundSize: '40px 40px',
												}}
												animate={{ backgroundPosition: ['0px 0px', '80px 0px'] }}
												transition={{ duration: 1.2, ease: 'linear', repeat: Infinity }}
											/>
											<div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-white/20" />
										</>
									)}
									<span className="relative z-10 flex items-center">
										{isGeneratingQuestion ? <Loader2 className="mr-2 size-5 animate-spin" /> : <Sparkles className="mr-2 size-5" />}
										{isGeneratingQuestion ? 'Generating...' : 'Generate Question'}
									</span>
								</Button>
							</div>
						)}

						<div className="flex justify-end gap-4">
							<Button variant="subtle" asChild>
								<Link to="/" viewTransition>
									Cancel
								</Link>
							</Button>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
								Save Quiz
							</Button>
						</div>
					</form>
				</FormProvider>
			</div>

			<AlertDialog
				open={blocker.state === 'blocked'}
				onOpenChange={(open) => {
					if (!open && blocker.state === 'blocked') {
						blocker.reset();
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
						<AlertDialogDescription>
							You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => blocker.reset?.()}>Stay</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								skipBlockerReference.current = true;
								blocker.proceed?.();
							}}
							variant="danger"
						>
							Leave
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{imageSelectionState.isOpen && (
				<ImageSelectionDialog
					open={imageSelectionState.isOpen}
					onOpenChange={(open) => setImageSelectionState((previous) => ({ ...previous, isOpen: open }))}
					selectedImage={imageSelectionState.currentImage}
					onSelectImage={(path) => {
						const index = imageSelectionState.questionIndex;
						if (index !== undefined && index !== null) {
							setValue(`questions.${index}.backgroundImage`, path, { shouldDirty: true });
							// Update local state to reflect change immediately if dialog stays open
							setImageSelectionState((previous) => ({ ...previous, currentImage: path }));
						}
					}}
					userId={userId}
				/>
			)}
		</>
	);
}
